import { Component, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { ContactApiService, EmailApiService } from '@aiwyn/api';
import { AuthAdaptorService, ContactUser } from '@aiwyn/auth';
import { Logger } from '@aiwyn/shared';

@Component({
  selector: 'app-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
  standalone: false,
})
export class GeneralComponent {

  // The email input form. Submitted 'onAddEmail()'
  saveEmailForm = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
  });

  emailAddresses = signal<string[]>(
    !(this.authAdaptor.user instanceof ContactUser)
      ? []
      : GeneralComponent._formatFwdEmailConfirmationAddresses(this.authAdaptor.user.getContact().fwdEmailConfirmationAddresses),
  );

  constructor(
    private emailApiService: EmailApiService,
    private contactApiService: ContactApiService,
    private readonly logger: Logger,
    public authAdaptor: AuthAdaptorService,
  ) {}

  /**
   * Toggle email forwarding.
   */
  toggleEmailForwarding(): void {
    const user = this.authAdaptor.user;
    if (!(user instanceof ContactUser)) {
      return;
    }

    user.getContact().fwdEmailConfirmation = !user.getContact().fwdEmailConfirmation;
    this.contactApiService.setForwardPaymentConfirmation(
      user.getContact().fwdEmailConfirmation,
      user.getContact().id
    ).pipe(first()).subscribe();
  }

  /**
   * Add email address on form submit if valid.
   */
  onAddEmail(): void {
    const user = this.authAdaptor.user;
    if (!(user instanceof ContactUser)) {
      this.logger.info('SETTINGS : GeneralComponent : onAddEmail : User is not a contact, not sure how we ended up here');
      return;
    }

    if (this.saveEmailForm.value && this.saveEmailForm.status === "VALID") {
      this.emailApiService.addCorrespondingEmail(this.saveEmailForm.value['email']).pipe(first()).subscribe((response) => {
        const emails = user.getContact().fwdEmailConfirmationAddresses;
        user.getContact().fwdEmailConfirmationAddresses = emails + (emails ? ',' : '') + response['email'];
        this.emailAddresses.set(GeneralComponent._formatFwdEmailConfirmationAddresses(user.getContact().fwdEmailConfirmationAddresses));
      });
      this.saveEmailForm.reset();
    }
  }

  /**
   * Makes API call to delete specified email in the user's forwarding list.
   * @param email
   */
  deleteEmail(email: string) {
    const user = this.authAdaptor.user;
    if (!(user instanceof ContactUser)) {
      this.logger.info('SETTINGS : GeneralComponent : deleteEmail : User is not a contact, not sure how we ended up here');
      return;
    }

    this.emailApiService.deleteCorrespondingEmail(email).pipe(first()).subscribe((response: Map<string, string>) => {
      const emailAddresses = GeneralComponent._formatFwdEmailConfirmationAddresses(user.getContact().fwdEmailConfirmationAddresses);
      const filteredEmailAddresses = emailAddresses.filter(email => email !== response['email']);
      user.getContact().fwdEmailConfirmationAddresses = filteredEmailAddresses.join(',');
      this.emailAddresses.set(filteredEmailAddresses);
    });
  }

  private static _formatFwdEmailConfirmationAddresses(fwdEmailConfirmationAddresses: string): string[] {
    return (fwdEmailConfirmationAddresses ?? '').split(',').filter(Boolean);
  }
}
